import { Link, useLocation } from "react-router-dom";
import { useRoleTheme } from "@/hooks/use-role-theme";
import {
  LayoutDashboard, Settings,
  UserCircle, Inbox, Package, BarChart2, CreditCard, Coins, Star, GraduationCap, Lock,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { isReleaseFlagOn } from "@/config/featureFlags";
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { AccountSwitcher } from "@/components/common/AccountSwitcher";

interface NavItem {
  label: string;
  icon: React.ElementType;
  href: string;
  badge?: number;
  iconColor: string;
  tourId?: string;
}

const mainNavItems: NavItem[] = [
  { label: "Dashboard",        icon: LayoutDashboard, href: "/teacher-dashboard",      iconColor: "text-indigo-500"   },
  { label: "My Profile",       icon: UserCircle,      href: "/teacher-profile-setup",  iconColor: "text-violet-500" },
  { label: "Student Inquiries",icon: Inbox,           href: "/teacher-inquiries",       iconColor: "text-amber-500",  tourId: "inquiries-nav" },
  { label: "My Offerings",     icon: Package,         href: "/teacher-offerings",       iconColor: "text-emerald-500", tourId: "offerings-nav" },
  { label: "Subscription",    icon: CreditCard,     href: "/teacher-subscription", iconColor: "text-indigo-500" },
  { label: "BMC Coins",        icon: Coins,           href: "/teacher-coins",           iconColor: "text-indigo-500"   },
  { label: "Analytics",        icon: BarChart2,       href: "/teacher-analytics",       iconColor: "text-rose-500"   },
  { label: "Reviews",          icon: Star,            href: "/teacher-reviews",         iconColor: "text-yellow-500" },
];

/** Tier is passed in so we can show a lock badge on the Classes item for SILVER/none */
type TeacherTier = "SILVER" | "GOLD" | "PLATINUM" | string | undefined;

interface TeacherSidebarProps {
  tier?: TeacherTier;
  teacherName: string;
  teacherEmail?: string;
  teacherAvatar: string;
  subjects?: string[];
}

export function TeacherSidebar({ teacherName, teacherAvatar, tier }: TeacherSidebarProps) {
  const location = useLocation();
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  useRoleTheme("teacher");

  const classManagementOn = isReleaseFlagOn("CLASS_MANAGEMENT");
  const isGoldOrPlatinum = tier === "GOLD" || tier === "PLATINUM";

  const NavItem = ({ item }: { item: NavItem }) => {
    const isActive = location.pathname === item.href;
    const Icon = item.icon;

    return (
      <SidebarMenuItem className="list-none">
        <Link
          to={item.href}
          title={isCollapsed ? item.label : undefined}
          data-tour={item.tourId}
          className={cn(
            "flex items-center gap-3 px-3 py-3 rounded-xl text-[15px] font-medium transition-all duration-150",
            isActive
              ? "bg-white/[0.12] text-white shadow-sm ring-1 ring-white/10"
              : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
            isCollapsed && "justify-center px-2.5"
          )}
        >
          <Icon className={cn("shrink-0", isActive ? "text-white" : item.iconColor)} style={{ width: "20px", height: "20px" }} />
          {!isCollapsed && (
            <span className="flex-1 truncate">{item.label}</span>
          )}
          {!isCollapsed && item.badge && (
            <span className={cn(
              "text-xs font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center",
              isActive
                ? "bg-white/20 text-white"
                : "bg-white/20 text-white"
            )}>
              {item.badge}
            </span>
          )}
        </Link>
      </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="border-r border-sidebar-border">
      <SidebarContent className="px-3 py-4 flex flex-col gap-4">
        {/* Profile */}
        <div className="flex items-center gap-2">
          <div className="flex-1 min-w-0">
            <AccountSwitcher currentRole="TEACHER" name={teacherName} avatar={teacherAvatar} />
          </div>
        </div>

        {/* Main nav */}
        <div className="flex flex-col gap-1">
          <div className={cn("flex items-center px-3 mb-1", isCollapsed ? "justify-center" : "justify-between")}>
            {!isCollapsed && (
              <p className="text-xs font-bold uppercase tracking-widest text-sidebar-foreground/40">Menu</p>
            )}
            {/* Collapse/expand toggle — desktop only; on mobile the sidebar is an
                off-canvas sheet and the opener lives in DashboardTopNav. */}
            <SidebarTrigger className="hidden md:flex h-6 w-6 shrink-0" />
          </div>
          <SidebarMenu className="gap-0.5">
            {mainNavItems.map(item => <NavItem key={item.href} item={item} />)}
            {classManagementOn && (
              <SidebarMenuItem className="list-none">
                <Link
                  to="/teacher-classes"
                  title={isCollapsed ? "Classes" : undefined}
                  className={cn(
                    "flex items-center gap-3 px-3 py-3 rounded-xl text-[15px] font-medium transition-all duration-150",
                    location.pathname.startsWith("/teacher-classes")
                      ? "bg-white/[0.12] text-white shadow-sm ring-1 ring-white/10"
                      : "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                    isCollapsed && "justify-center px-2.5"
                  )}
                >
                  <GraduationCap
                    className={cn("shrink-0", location.pathname.startsWith("/teacher-classes") ? "text-white" : "text-amber-500")}
                    style={{ width: "20px", height: "20px" }}
                  />
                  {!isCollapsed && (
                    <>
                      <span className="flex-1 truncate">Classes</span>
                      {!isGoldOrPlatinum && (
                        <Lock className="w-3.5 h-3.5 text-sidebar-foreground/40 shrink-0" aria-label="Requires Gold or Platinum" />
                      )}
                    </>
                  )}
                </Link>
              </SidebarMenuItem>
            )}
          </SidebarMenu>
        </div>

      </SidebarContent>

      {/* Footer */}
      <SidebarFooter className="px-3 py-3 border-t border-sidebar-border">
        <SidebarMenu className="gap-0.5">
          <SidebarMenuItem className="list-none">
            <Link
              to="/settings"
              title={isCollapsed ? "Settings" : undefined}
              className={cn(
                "flex items-center gap-3 px-3 py-3 rounded-xl text-[15px] font-medium transition-all duration-150",
                "text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground hover:text-white",
                isCollapsed && "justify-center px-2.5"
              )}
            >
              <Settings style={{ width: "20px", height: "20px" }} className="shrink-0" />
              {!isCollapsed && <span>Settings</span>}
            </Link>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  );
}
